import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { useInView } from 'react-intersection-observer';
import CountUp from 'react-countup';
import { LOGOS } from '@/lib/constants';
import { motion } from 'framer-motion';

function FadeIn({ children, delay = 0 }) {
  const { ref, inView } = useInView({ threshold: 0.15, triggerOnce: true });
  return <motion.div ref={ref} initial={{ opacity: 0, y: 24 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay, ease: 'easeOut' }}>{children}</motion.div>;
}

export default function RebrandPage() {
  return (
    <div className="overflow-hidden">
      {/* HERO */}
      <section className="relative min-h-[70vh] flex items-center bg-[#1A1008] overflow-hidden">
        <div className="noise-overlay absolute inset-0 pointer-events-none" />
        <div className="relative z-10 mx-auto w-full max-w-[1400px] px-6 sm:px-8 lg:px-12 py-24 lg:py-32">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <div className="flex items-center justify-center gap-10 mb-12">
                <img src={LOGOS.hrAnexiWhite} alt="HR Anexi" className="h-12 lg:h-14 opacity-60" />
                <ArrowRight size={24} className="text-[#E8642C] flex-shrink-0" />
                <img src={LOGOS.whiteColored} alt="Anexi Consulting" className="h-32 lg:h-40" />
              </div>
              <h1 className="text-[clamp(2.5rem,6vw,5.5rem)] font-bold text-white leading-[0.92] tracking-[-0.02em] mb-6">
                Same fire.<br/><span className="text-[#E8642C]">Bigger stage.</span>
              </h1>
              <h2 className="text-xl text-white/60 mb-8">HR Anexi is now Anexi Consulting.</h2>
              <p className="text-[15px] text-white/40 leading-[1.8] max-w-2xl mx-auto">
                After 18 years of transforming organisations as HR Anexi, we've outgrown our name. Not our values. Not our commitment. Not our fire. Just our name, because what we do today goes far beyond HR.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* LETTER */}
      <section className="py-24 lg:py-32 bg-[#FAF5EF]">
        <div className="mx-auto w-full max-w-[1400px] px-6 sm:px-8 lg:px-12">
          <FadeIn>
            <div className="max-w-3xl">
              <h2 className="text-3xl font-bold text-[#1A1008] mb-8 tracking-tight">A letter from our leadership.</h2>
              <div className="space-y-5 text-[15px] text-[#2D2019] leading-[1.8]">
                <p>When Dr. Ashish Arora founded this firm in 2007, the vision was clear: help organisations transform from the inside out. We started as an HR consulting firm, and that's where our journey began.</p>
                <p>But over 18 years, our work evolved. We moved beyond HR into organisation design, leadership development, experiential learning, employee engagement research, and now AI transformation. Our clients stopped seeing us as "HR consultants" years ago. It was time our name caught up.</p>
                <p><strong className="text-[#1A1008]">Anexi Consulting</strong> reflects who we are today: a full-spectrum organisation transformation firm that combines strategy, learning, experience, research, and technology to help businesses build what's next.</p>
                <p>The mountain in our logo represents the ascent every organisation must make. The layered colours represent the depth of expertise we bring. And the warmth? That's the human touch that makes us different.</p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* WHAT CHANGED */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="mx-auto w-full max-w-[1400px] px-6 sm:px-8 lg:px-12">
          <FadeIn>
            <h2 className="text-3xl font-bold text-[#1A1008] mb-12 text-center tracking-tight">What Changed, What Didn't.</h2>
          </FadeIn>
          <div className="grid sm:grid-cols-2 gap-5 max-w-4xl mx-auto">
            <FadeIn>
              <div className="bg-[#FAF5EF] rounded-2xl p-8 border border-[rgba(61,28,17,0.04)] h-full">
                <h3 className="text-lg font-semibold text-[#E8642C] mb-5">What Changed</h3>
                <ul className="space-y-3">
                  {['Our name', 'Our brand identity', 'Our visual language', 'Our positioning, now reflecting the full scope of what we do'].map(item => (
                    <li key={item} className="flex items-start gap-3 text-[14px] text-[#2D2019]">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#E8642C] mt-2 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </FadeIn>
            <FadeIn delay={0.1}>
              <div className="bg-[#FAF5EF] rounded-2xl p-8 border border-[rgba(61,28,17,0.04)] h-full">
                <h3 className="text-lg font-semibold text-[#1A1008] mb-5">What Didn't</h3>
                <ul className="space-y-3">
                  {['Our people', 'Our methodology', 'Our commitment to research-backed transformation', 'Our clients and relationships', 'Our fire, literal and metaphorical'].map(item => (
                    <li key={item} className="flex items-start gap-3 text-[14px] text-[#2D2019]">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#1A1008] mt-2 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* NUMBERS */}
      <RebrandNumbers />

      {/* CTA */}
      <section className="py-24 lg:py-28 bg-[#FAF5EF]">
        <div className="mx-auto w-full max-w-[1400px] px-6 sm:px-8 lg:px-12 text-center">
          <FadeIn>
            <h2 className="text-3xl lg:text-4xl font-bold text-[#1A1008] mb-4 tracking-tight">Ready to see what's new?</h2>
            <p className="text-[15px] text-[#6B5B50] mb-10">Explore the full Anexi Consulting experience.</p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/" className="group bg-[#1A1008] text-white px-8 py-4 rounded-full text-[14px] font-medium tracking-wide hover:bg-[#3D1C11] transition-all flex items-center gap-2.5">
                Explore the New Anexi Consulting <ArrowUpRight size={15} className="opacity-60 group-hover:opacity-100 transition-opacity" />
              </Link>
              <Link to="/contact" className="text-[#3D1C11] px-8 py-4 rounded-full text-[14px] font-medium tracking-wide border border-[#3D1C11]/15 hover:bg-white transition-all">
                Talk to Us
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}

function RebrandNumbers() {
  const { ref, inView } = useInView({ threshold: 0.3, triggerOnce: true });
  const metrics = [
    { value: 739, suffix: '+', label: 'Organisations' },
    { value: 500000, suffix: '+', label: 'Lives Impacted' },
    { value: 18, suffix: '+', label: 'Years' },
    { value: 96.4, suffix: '%', label: 'Client Satisfaction', decimals: 1 },
  ];

  return (
    <section ref={ref} className="bg-[#1A1008] py-20 lg:py-28 relative">
      <div className="noise-overlay absolute inset-0 pointer-events-none" />
      <div className="relative z-10 mx-auto w-full max-w-[1400px] px-6 sm:px-8 lg:px-12">
        <h2 className="text-2xl font-bold text-white mb-12 text-center tracking-tight">The Numbers That Earned This.</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {metrics.map((m, i) => (
            <div key={i} className="text-center">
              <p className="text-[clamp(2rem,5vw,3.5rem)] font-bold text-white tracking-tight leading-none mb-2">
                {inView ? <CountUp end={m.value} duration={2} decimals={m.decimals || 0} separator="," suffix={m.suffix} /> : '0'}
              </p>
              <p className="text-[13px] text-white/35 tracking-wide">{m.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
